import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { NextImage, RichText, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect } from 'react';
import { setContext } from '../../lib/utils/coveo/headlessContext';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';

import {
  StBannerContainer,
  StBannerContent,
  StBannerWrapper,
  StRichTextGridItem,
} from './HeroAndSearch.styles';
import SearchForm from './SearchForm';
import { HeroAndSearchType } from './HeroAndSearch.types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { setDayOfBirth } from '../../features/coveoSlice';
import UiHeading from '../shared/Heading';
import UiBox from '../shared/Box';

const HeroAndSearch = (props: HeroAndSearchType): JSX.Element => {
  const isVariant = props?.params?.variant === 'variant2';
  const theme = useTheme();
  const { sitecoreContext }: any = useSitecoreContext();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;
  const dispatch = useDispatch();
  const route = sitecoreContext?.route;
  const isFeaturedTrial =
    (route?.placeholders['jss-main'] as any)?.findIndex(
      (item: any) => item?.componentName === 'FeaturedTrial'
    ) > -1
      ? true
      : false;

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    const userInterests = userProfile?.profileData?.conditions?.map(
      (item: any) => item.conditionValue
    );
    if (profileDetails) {
      setContext({
        gender: profileDetails?.gender,
        dateOfBirth: profileDetails?.dob,
        interests: userInterests ? userInterests : [],
      });
    } else {
      setContext({
        gender: '',
        dateOfBirth: '',
        interests: '',
      });
    }
  }, [profileDetails?.dob, profileDetails?.gender, userProfile?.profileData?.conditions]);

  useEffect(() => {
    if (profileDetails?.dob) {
      dispatch(setDayOfBirth(String(new Date(profileDetails?.dob).getFullYear())));
    }
  }, [dispatch, profileDetails?.dob]);

  if (!props?.fields) return <></>;

  return (
    <StBannerWrapper
      id="hero-and-search"
      className={
        isFeaturedTrial ? (isDesktop && !isEditorActive() ? 'overlap-featured-trials' : '') : ''
      }
      sx={{
        ...(isFeaturedTrial && {
          marginBottom:
            isMobile || isTablet ? 'unset' : props?.fields?.marginBottom?.value || '400px',
        }),
      }}
    >
      <NextImage
        priority={true} // When true, the image will be considered high priority and preload
        field={
          isDesktop
            ? props?.fields?.desktopBGImage
            : isTablet
              ? props?.fields?.tabletBGImage
              : isMobile
                ? props?.fields?.mobileBGImage
                : props?.fields?.desktopBGImage
        }
        style={{ zIndex: 1, objectFit: 'cover', objectPosition: 'center' }}
        fill={true}
      />
      <StBannerContainer
        id="hero-and-search-content"
        className="ui-hero-and-search__content"
        container
      >
        <StBannerContent
          container
          item
          xs={12}
          sm={12}
          md={isVariant ? 7 : 6}
          lg={isVariant ? 7 : 6}
          isVariant={isVariant}
          data-testid="hero-and-search-banner-content"
        >
          <Grid
            item
            sx={{
              [theme.breakpoints.up('sm')]: {
                maxWidth: isVariant ? 'none' : '450px',
                margin: isVariant ? '0px 0 17px 20px 17px' : '0 auto',
              },
            }}
          >
            <UiBox styles={{ paddingBottom: '8px' }}>
              <UiHeading
                size="h1"
                data-testid="hero-and-search-title"
                styles={{
                  fontSize: props.fields.fontSize?.value || '30px',
                  lineHeight: props.fields.lineHeight?.value,
                  color: (theme.palette as any)?.fontTitleColor1?.main,
                }}
              >
                {props.fields.heading}
              </UiHeading>
            </UiBox>
          </Grid>
          <StRichTextGridItem item isVariant={isVariant}>
            <RichText field={props?.fields?.content} data-testid="hero-and-search-content" />
          </StRichTextGridItem>
        </StBannerContent>

        <SearchForm {...props} />
      </StBannerContainer>
    </StBannerWrapper>
  );
};

export default HeroAndSearch;
